"use client";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo } from "react";

import useRouter from "@stadium/hooks/lib/use-custom-route";
import isEmpty from "lodash/isEmpty";

import { CurrentUserContext } from "@stadium/hooks/lib/use-current-user";

import { useAuthQuery } from "@stadium/services/apollo-client";
import { useFlags } from "@stadium/services/launch-darkly";

import { FetchWorkspaceBySlugDocument } from "@stadium/hooks/graphql/operations";

import {
  getShopUrlWithCurrentWorkspaceSlug as getShopUrlWithCurrentWorkspaceSlugHelper,
  getWorkspaceUrl,
} from "@stadium/helpers/routes";
import { isAdminOrOwner } from "@stadium/helpers/roles";

import {
  roleBasedWorkspacePermissionsFlag,
  workspaceLaunchFlag,
  workspaceRouteUpdateFlag,
} from "@stadium/services/launch-darkly/constants";

import {
  MY_WORKSPACE_SLUG,
  WORKSPACE_SLUG_REQUEST_HEADER_KEY,
  DEFAULT_WORKSPACE_ROUTE,
} from "@stadium/constants";
import { MY_WALLET_DATA, POINTS_WALLET_DATA } from "@stadium/constants/wallets";
import { GIFTING_BASIC_INTENT, TRIAL_PACKAGE_STATUS } from "@stadium/constants/feature-packages";
import { owner } from "@stadium/constants/lib/contacts";
import {
  SHOP_CATALOG_PATH_SUFFIX,
  CREATE_NEW_WORKSPACE_ROUTE,
  CREATE_WORKSPACE_PATH,
} from "@stadium/constants/routes";
import useFetchWorkspaces from "@stadium/hooks/lib/use-fetch-workspaces";

import type { BeforeNavigateCallback } from "@stadium/hooks/lib/app-router-provider";

const PERSONAL_WORKSPACE_ROLE = "personalWorkspace";
interface WorkspaceContextType {
  workspace: string;
  workspaceData: any;
  workspaceDataLoading: boolean;
  workspacePermissions: any;
  workspaceSlug: string;
  workspaces: any[];
  refetchWorkspaces: () => void;
  getShopUrlWithCurrentWorkspaceSlug: (url: string) => string;
  getLockerShopUrlWithCurrentWorkspaceSlug: (url: string, suffix?: string) => string;
  isOwner?: boolean;
  isGiftingBasicPackage?: boolean;
}

const defaultContextValue = {
  workspace: "",
  workspaceData: {},
  workspaceDataLoading: false,
  workspacePermissions: {},
  workspaceSlug: "",
  workspaces: [],
  getShopUrlWithCurrentWorkspaceSlug: (url: string) => url,
  getLockerShopUrlWithCurrentWorkspaceSlug: (url: string) => url,
};

const emptyArr = [];
const emptyObj = {};

export const WorkspaceContext = createContext<WorkspaceContextType>(defaultContextValue);

export const useWorkspaceContext = () => useContext(WorkspaceContext);

const WorkspaceProvider = ({ children }) => {
  const router = useRouter();
  const {
    currentUserData,
    currentUserConfig = {},
    shouldSkipWorkspaceValidation,
  } = useContext(CurrentUserContext);

  const { isDesigner = false } = currentUserConfig;

  const { workspaceSlug } = router.query;
  const {
    workspaces,
    loading: workspacesLoading,
    called: workspacesFetched,
    refetch: refetchWorkspaces,
  } = useFetchWorkspaces();

  const {
    [workspaceRouteUpdateFlag]: workspaceRouteUpdateFlagEnabled,
    [workspaceLaunchFlag]: workspaceLaunchFlagEnabled,
    [roleBasedWorkspacePermissionsFlag]: roleBasedWorkspacePermissions = emptyObj,
  } = useFlags();

  const {
    data: workspaceData,
    loading: workspaceDataLoading,
    refetch: refetchWorkspace,
    error,
  } = useAuthQuery(FetchWorkspaceBySlugDocument, {
    variables: {
      slug: workspaceSlug,
      includeUserDetails: true,
      includeWorkspaceDetails: workspaceLaunchFlagEnabled,
    },
    context: {
      headers: {
        [WORKSPACE_SLUG_REQUEST_HEADER_KEY]: workspaceSlug,
      },
    },
    skip:
      !workspaceLaunchFlagEnabled ||
      !workspaceSlug ||
      (isDesigner && shouldSkipWorkspaceValidation),
  });

  const currentWorkspaceUser = workspaceData?.currentWorkspaceUser || emptyObj;
  const hasWorkspaceDataFetched = !isEmpty(workspaceData);

  useEffect(() => {
    if (error && !(isDesigner && shouldSkipWorkspaceValidation)) {
      router.replace(DEFAULT_WORKSPACE_ROUTE);
    }
  }, [error, shouldSkipWorkspaceValidation, isDesigner]);

  const isOnCreateWorkspaceFlow = router.pathname?.includes(CREATE_WORKSPACE_PATH);

  useEffect(() => {
    if (
      workspacesFetched &&
      !workspacesLoading &&
      workspaces.length === 0 &&
      !isOnCreateWorkspaceFlow
    ) {
      router.replace(CREATE_NEW_WORKSPACE_ROUTE);
    }
  }, [workspacesFetched, workspacesLoading, workspaces.length, isOnCreateWorkspaceFlow]);

  const { permissions: rawWorkspacePermissions = emptyObj, roles = [] } =
    workspaceData?.currentWorkspaceUser || emptyObj;

  const isPersonalWorkspace = workspaceSlug === MY_WORKSPACE_SLUG;

  const currentUserRole = isPersonalWorkspace
    ? PERSONAL_WORKSPACE_ROLE
    : roles?.[0]?.toLowerCase?.();

  const workspacePermissions = useMemo(() => {
    const roleFlagPermissions = roleBasedWorkspacePermissions?.[currentUserRole];

    if (!roleFlagPermissions || isEmpty(roleFlagPermissions)) {
      return rawWorkspacePermissions;
    }

    const permissions = { ...rawWorkspacePermissions };

    Object.entries(roleFlagPermissions).forEach(([permissionId, flagPermission]) => {
      if (flagPermission == null) return;

      permissions[permissionId] = {
        ...(permissions[permissionId] || emptyObj),
        ...flagPermission,
      };
    });

    return permissions;
  }, [rawWorkspacePermissions, roleBasedWorkspacePermissions, currentUserRole]);

  const workspace = useMemo(() => {
    const workspace = workspaceData?.workspace || emptyObj;
    return {
      ...workspace,
      isPersonalWorkspace,
      contactsPresent:
        workspace.membersCount +
          workspace.sendersCount +
          workspace.adminsCount +
          workspace.nonMembersCount >
        1,
    };
  }, [workspaceData]);

  const { walletBalance, walletCurrency, teams: oldTeams, firstName } = currentUserData || emptyObj;

  const teams = (workspaceLaunchFlagEnabled ? currentWorkspaceUser?.teams : oldTeams) || emptyArr;

  const pointsWallet = useMemo(() => {
    return {
      ...POINTS_WALLET_DATA,
      name: isPersonalWorkspace ? "My Points Wallet" : `${firstName}'s Points Wallet`,
      myWalletBalance: currentWorkspaceUser?.workspacePoints,
      unallocatedWalletBalance: workspace?.unallocatedPoints,
      currentUserRole: isPersonalWorkspace ? "" : "owner",
    };
  }, [currentWorkspaceUser, workspace]);

  const currentWorkspaceWallet = useMemo(() => {
    return isPersonalWorkspace
      ? {
          ...MY_WALLET_DATA,
          myWalletBalance: walletBalance,
          currencyCode: walletCurrency?.code,
          permissions: {
            add_funds: true,
          },
        }
      : teams.find(({ defaultTeam }) => defaultTeam);
  }, [teams, walletCurrency, walletBalance, walletCurrency]);

  const teamsWithoutDefault = useMemo(
    () => teams.filter(({ defaultTeam }) => !defaultTeam),
    [teams],
  );

  const sharedWalletData = [currentWorkspaceWallet, pointsWallet, ...teamsWithoutDefault].filter(
    Boolean,
  );

  const onWorkspaceBeforeNavigate = useCallback<BeforeNavigateCallback>(
    (path) => {
      if (!workspaceRouteUpdateFlagEnabled || !workspaceSlug) return;
      const rewritten = getWorkspaceUrl({
        url: path,
        workspaceSlug,
        pathname: window.location.pathname,
        prependWorkspaceSlug: true,
      });
      if (typeof rewritten !== "string" || rewritten === path) return;
      return rewritten;
    },
    [workspaceRouteUpdateFlagEnabled, workspaceSlug],
  );

  useLayoutEffect(() => {
    const ev = router.events;
    if (!ev || !workspaceRouteUpdateFlagEnabled || !workspaceSlug) return;
    ev.on("beforePush", onWorkspaceBeforeNavigate);
    ev.on("beforeReplace", onWorkspaceBeforeNavigate);
    return () => {
      ev.off("beforePush", onWorkspaceBeforeNavigate);
      ev.off("beforeReplace", onWorkspaceBeforeNavigate);
    };
  }, [router.events, workspaceRouteUpdateFlagEnabled, workspaceSlug, onWorkspaceBeforeNavigate]);

  const getShopUrlWithCurrentWorkspaceSlug = useCallback(
    (url: string) => getShopUrlWithCurrentWorkspaceSlugHelper(url, workspaceSlug),
    [workspaceSlug],
  );

  const getLockerShopUrlWithCurrentWorkspaceSlug = useCallback(
    (url: string, suffix: string = SHOP_CATALOG_PATH_SUFFIX) => {
      const shopUrl = getShopUrlWithCurrentWorkspaceSlugHelper(url, workspaceSlug);
      return `${shopUrl}${suffix}`;
    },
    [workspaceSlug],
  );

  const { subscription = {} } = workspace;

  const isFreeTrial = subscription?.status === TRIAL_PACKAGE_STATUS;
  const isActivePackageUser = !isFreeTrial && subscription?.daysLeft > 0;
  const isGiftingBasicPackage = workspace?.package?.intent === GIFTING_BASIC_INTENT;

  const value = useMemo(
    () => ({
      workspace,
      workspaces,
      refetchWorkspaces,
      workspaceData,
      workspaceDataLoading,
      workspacePermissions,
      workspaceSlug,
      teams,
      sharedWalletData,
      currentWorkspaceWallet,
      teamsWithoutDefault,
      currentWorkspaceUser,
      canUpgrade: isAdminOrOwner(roles),
      getLockerShopUrlWithCurrentWorkspaceSlug,
      getShopUrlWithCurrentWorkspaceSlug,
      activePackage: workspace?.package || emptyObj,
      refetchWorkspace,
      isFreeTrial,
      isOwner: roles?.[0] === owner,
      isActivePackageUser,
      hasWorkspaceDataFetched,
      isGiftingBasicPackage,
      role: roles?.[0],
    }),
    [
      workspaces,
      refetchWorkspaces,
      workspaceSlug,
      workspaceData,
      workspaceDataLoading,
      workspacePermissions,
      teams,
      sharedWalletData,
      currentWorkspaceUser,
      getLockerShopUrlWithCurrentWorkspaceSlug,
      getShopUrlWithCurrentWorkspaceSlug,
      workspace,
      isFreeTrial,
      roles,
    ],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export default WorkspaceProvider;
